import { PageHeading } from '@quad/bootstrap-react';
import { useState, useEffect } from 'react';
import { Container, Row, Stack, Button, ListGroup, Toast, ToastContainer } from 'react-bootstrap';
import appConfig from '../../appConfig';
import { AppEnvironment } from '../../constants/AppEnvironment';
import { AdGroupSearch } from '../../components/Permissions/AdGroupSearch';
import { ActionAlert } from '../../components/UIExtentions/ActionAlert';
import ScrollPageContent from '../shared/ScrollPageContent';
import AppManagerClient from '../../api/AppManagerClient';
import Select, { OnChangeValue } from 'react-select';
import { OperatingSystemType } from '../../constants/OperatingSystemType';
import { capitalizeFirstLetter } from '../../helpers/DisplayHelpers';

export const AgentInstallationView = (props: any) => {
    const baseUrl = appConfig.apiBaseUrl;
    const isProduction = appConfig.appEnvironment === AppEnvironment.Production;
    const [installScript, setInstallScript]: [string, Function] = useState('');
    const [showCopyToast, setShowCopyToast]: [boolean, Function] = useState(false);
    const [serverOwnerSid, setServerOwnerSid] = useState<string>('');
    const [serverOwnerSidValid, setServerOwnerSidValid] = useState<boolean>(false);
    const [operatingSystem, setOperatingSystem] = useState<OperatingSystemType>(OperatingSystemType.Windows);

    const windowsScriptLines = [];
    windowsScriptLines.push('$installerUrl = "baseUri-placeholder/Agents/installer/download/latest"');
    windowsScriptLines.push('$jwtToken="jwt-placeholder"');
    windowsScriptLines.push("$ProgressPreference = 'SilentlyContinue'");
    windowsScriptLines.push('Write-Host "=========================="');
    windowsScriptLines.push('Write-Host "This script will install the AppManager Agent by:"');
    windowsScriptLines.push('Write-Host "1. Downloading the installer from $installerUrl."');
    windowsScriptLines.push('Write-Host "2. Running the installer."');
    windowsScriptLines.push('$confirm = Read-Host "Are you sure you want to continue? (y/n)"');
    windowsScriptLines.push('if($confirm -ne \'y\'){ Write-Host "User cancelled"; return; }');
    windowsScriptLines.push('Write-Host "=========================="');
    windowsScriptLines.push('Write-Host "Downloading Installer..."');
    windowsScriptLines.push('iwr -uri "$installerUrl" -OutFile .\\installer.exe -Headers @{ "Authorization" = "Bearer $jwtToken" }');
    windowsScriptLines.push('if(-not (Test-Path -Path .\\Installer.exe)) { Write-Warning "Downloaded Installer.exe not found"; return; }');
    windowsScriptLines.push('Write-Host "Running installer..."');
    windowsScriptLines.push('.\\Installer.exe jwt="$jwtToken" serverOwnerSID="serverOwnerSID-placeholder" endpointOverride-placeholder');
    windowsScriptLines.push('echo "Done."');

    const windowsBaseScript = windowsScriptLines.join(';');

    const linuxScriptLines = [];
    linuxScriptLines.push('installerUrl="baseUri-placeholder/Agents/installer/download/latest?osType=linux"');
    linuxScriptLines.push('jwtToken="jwt-placeholder"');
    linuxScriptLines.push('installer="/tmp/appman-agent-installer"');
    // function choose_privilege_command()
    linuxScriptLines.push('choose_privilege_command() { local dzdo_available=false');
    linuxScriptLines.push('local sudo_available=false');
    linuxScriptLines.push('if command -v dzdo &> /dev/null; then dzdo_available=true; fi');
    linuxScriptLines.push('if command -v sudo &> /dev/null; then sudo_available=true; fi');
    linuxScriptLines.push('if [[ "$dzdo_available" == true ]]; then PRIV_CMD="dzdo"');
    linuxScriptLines.push('elif [[ "$sudo_available" == true ]]; then PRIV_CMD="sudo"');
    linuxScriptLines.push('else echo "Neither dzdo nor sudo command found. Please install one of them to proceed."; exit 1; fi');
    linuxScriptLines.push('}');
    // end function
    linuxScriptLines.push('choose_privilege_command');
    linuxScriptLines.push('echo "=========================="');
    linuxScriptLines.push('echo -e "This script will install the AppManager Agent by:"');
    linuxScriptLines.push('echo -e "1. Downloading the installer from $installerUrl."');
    linuxScriptLines.push('echo -e "2. Running the installer using \'$PRIV_CMD\' as the privilege escalation command."');
    linuxScriptLines.push('echo "=========================="');
    linuxScriptLines.push('echo');
    linuxScriptLines.push('read -p "Do you want to continue? (y/n): " confirm; if [[ "$confirm" != "y" ]]; then echo "User cancelled"; exit 1; fi');
    linuxScriptLines.push('echo "Starting AppManager Agent installation..."');
    linuxScriptLines.push('echo "Downloading Installer..."');
    linuxScriptLines.push('$PRIV_CMD curl --progress-bar -H "Authorization: Bearer $jwtToken" -o "$installer" $installerUrl');
    linuxScriptLines.push('if [[ ! -f "$installer" ]]; then echo "Downloaded installer not found" >&2; exit 1; fi');
    linuxScriptLines.push('echo "Running installer..."');
    linuxScriptLines.push('$PRIV_CMD chmod +x "$installer"');
    linuxScriptLines.push('$PRIV_CMD "$installer" jwt="$jwtToken" serverOwnerSID="serverOwnerSID-placeholder" endpointOverride-placeholder');
    linuxScriptLines.push('echo "Done."');

    const linuxBaseScript = linuxScriptLines.join(';');

    function copyScript() {
        navigator.clipboard.writeText(installScript);
        setShowCopyToast(true);
    }

    useEffect(() => {
        const constructInstallScript = async () => {
            let baseScript = '';
            switch (operatingSystem) {
                case OperatingSystemType.Windows:
                    baseScript = windowsBaseScript;
                    break;
                case OperatingSystemType.Linux:
                    baseScript = linuxBaseScript;
                    break;
                default:
                    throw new Error(`Unsupported operating system: ${operatingSystem}`);
            }

            const tokenResult = await AppManagerClient.getAccessToken();
            let script = baseScript.replaceAll('jwt-placeholder', tokenResult).replaceAll('baseUri-placeholder', baseUrl).replaceAll('serverOwnerSID-placeholder', serverOwnerSid);
            if (isProduction) {
                script = script.replaceAll('endpointOverride-placeholder', '');
            } else {
                script = script.replaceAll('endpointOverride-placeholder', 'apiBaseEndpoint='.concat(appConfig.apiBaseUrl).concat('/'));
            }
            setInstallScript(script);
        };

        constructInstallScript();
    }, [isProduction, baseUrl, serverOwnerSid, operatingSystem, windowsBaseScript, linuxBaseScript]);

    return (
        <ScrollPageContent>
            <PageHeading>
                <div className="col">
                    <h1>
                        <i className="fa fa-download"></i> Agent Installation
                    </h1>
                </div>
            </PageHeading>
            <Stack>
                <Container>
                    <ToastContainer style={{ zIndex: 999 }} position="middle-center">
                        <Toast onClose={() => setShowCopyToast(false)} show={showCopyToast} delay={4000} autohide bg="white">
                            <Toast.Header>
                                <strong className="me-auto">Success!</strong>
                            </Toast.Header>
                            <Toast.Body>Installation script copied successfully.</Toast.Body>
                        </Toast>
                    </ToastContainer>
                    <Row>
                        <ListGroup as="ol" numbered>
                            <ListGroup.Item as="li">
                                Select a server owner to generate the installation script.
                                <div className="form-group" style={{ marginTop: '10px' }}>
                                    <AdGroupSearch
                                        focus={true}
                                        onChange={(adGroupName: string, adGroupSid: string) => {
                                            setServerOwnerSid(adGroupSid);
                                            const isServerOwnerValid = !(adGroupSid === undefined || adGroupSid.length === 0);
                                            setServerOwnerSidValid(isServerOwnerValid);
                                        }}
                                    />
                                </div>
                                <ActionAlert key="owner-alert" variant="warning">
                                    Only group members one level deep will be set as server owners. Sub-group members will not be included.
                                </ActionAlert>
                            </ListGroup.Item>
                            <ListGroup.Item as="li" hidden={!serverOwnerSidValid}>
                                Select a server operating system.
                                <div className="form-group" style={{ marginTop: '10px' }}>
                                    <Select
                                        id="os-select"
                                        options={[
                                            { label: capitalizeFirstLetter(OperatingSystemType.Windows), value: OperatingSystemType.Windows },
                                            { label: capitalizeFirstLetter(OperatingSystemType.Linux), value: OperatingSystemType.Linux },
                                        ]}
                                        value={{ label: capitalizeFirstLetter(operatingSystem), value: operatingSystem }}
                                        onChange={async (e: OnChangeValue<any, boolean>) => {
                                            setOperatingSystem(e.value);
                                        }}
                                    />
                                </div>
                            </ListGroup.Item>
                            <ListGroup.Item as="li" hidden={!serverOwnerSidValid}>
                                Click
                                <div className="shell-command">
                                    <Button className="command-text" variant="green" size="sm" onClick={copyScript}>
                                        {installScript}
                                    </Button>
                                </div>
                                to copy the installation script.
                                <ActionAlert key="PAT-alert" variant="warning">
                                    The Personal Access Token of the logged in user is pre-inserted in the script and will expire 1 hour after the start of your session, rendering
                                    the copied script unusable thereon.
                                </ActionAlert>
                            </ListGroup.Item>

                            <ListGroup.Item as="li" hidden={operatingSystem !== OperatingSystemType.Windows}>
                                Run the script in administrative powershell.
                            </ListGroup.Item>

                            <ListGroup.Item as="li" hidden={operatingSystem !== OperatingSystemType.Linux}>
                                Run the script in bash.
                            </ListGroup.Item>

                            <ListGroup.Item as="li" hidden={!serverOwnerSidValid}>
                                Once installed, the agent will immediately discover applications and make them available at <a href={baseUrl}>{baseUrl}</a>.
                            </ListGroup.Item>
                        </ListGroup>
                    </Row>
                </Container>
            </Stack>
        </ScrollPageContent>
    );
};
